import React, { useRef, useState } from "react";

import { Button } from "@UI/Components/Button/Button";
import { Icon } from "@UI/Components/Icon/Icon";
import { Envelope } from "@UI/Components/Icons/Envelope";
import {
  Input,
  InputGroup,
  InputLeftElement,
} from "@UI/Components/Input/Input";
import { Link } from "@UI/Components/Link/Link";
import { Text } from "@UI/Components/Text/Text";
import { VStack } from "@UI/Components/Stack/Stack";
import { useToast } from "@UI/hooks/useToast";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useI18n from "@I18n/hooks/useI18n";
import useForm from "@Libs/Utils/hooks/useForm";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import {
  useEmailSuggestion,
  useUserAvailability,
} from "@modules/Authentication/hooks/useEmailLogin";
import { isUserNotFound } from "@modules/Authentication/services/emailLogin";

import { serviceRequestPathFragment } from "../../../config/routes";
import { AuthFormControl } from "../../Components/AuthFormControl/AuthFormControl";
import {
  authFieldStyles,
  authSubmitStyles,
} from "../../Components/AuthFormControl/authStyles";
import { EmailSuggestion } from "../../Components/EmailSuggestion/EmailSuggestion";
import { SsoButtons } from "../../Components/SsoButtons/SsoButtons";
import { SsoMissingInfoModal } from "../../Components/SsoMissingInfoModal/SsoMissingInfoModal";
import { TaskCreationOutcomeModals } from "../../Components/TaskCreationOutcomeModals/TaskCreationOutcomeModals";
import { AUTH_SCREEN_KEYS, EMAIL_PATTERN } from "../../config/constants";
import { setAuthIdentity } from "../../hooks/useAuthIdentity";
import { useSsoSignIn } from "../../hooks/useSsoSignIn";
import messages from "../../messages";
import { AuthStepLayout } from "../AuthStepLayout/AuthStepLayout";

const EMAIL_FIELD_ID = "taskCreationAuthEmail";
/** Fixed so a repeated failure replaces its toast instead of stacking. */
const LOOKUP_FAILED_TOAST_ID = "taskCreation.auth.email.lookupFailed";

interface IEmailFormValues {
  email: string;
}

/** One value, so a correction cannot drift from the address it is about. */
interface ISuggestion {
  address: string;
  correction: string;
}

/**
 * One address, which decides whether the visitor logs in or registers: the
 * availability endpoint's first name or 404 is the branch.
 *
 * A likely typo is offered as a correction first. Continue waits for an answer
 * but never forces one — "No" keeps the address as typed.
 *
 * A provider sign-in skips both branches: it authenticates in one step, so this
 * screen is also where that flow ends, with the publish the other two finish on.
 */
export const AuthEmailScreen = () => {
  const { formatMessage, locale } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();

  // By language, not locale, like Rails' `LanguageHelper.language`: fr/nl are
  // the only prefixes nginx rewrites, so the full locale would 404.
  const language = locale.split("-")[0];

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    // Covers the whole of Continue and nothing else: the blur check shares a
    // mutation, and disabling Continue mid-blur would swallow the click.
    formState: { errors, isSubmitting },
  } = useForm<IEmailFormValues>({ defaultValues: { email: "" } });

  const {
    signIn,
    pendingProvider,
    missingInfo,
    submitMissingInfo,
    dismissMissingInfo,
    isPublishing,
    publishedRequestId,
  } = useSsoSignIn();

  const [suggestion, setSuggestion] = useState<ISuggestion>();
  const { mutateAsync: checkSuggestion } = useEmailSuggestion();
  const { mutateAsync: checkAvailability } = useUserAvailability();

  /**
   * The address whose correction was declined. Without it the next Continue
   * puts the same banner back, with no way past it.
   */
  const dismissedAddress = useRef<string>();

  const goTo = (screenKey: string) =>
    navigate(serviceRequestPathFragment.SERVICE_REQUEST_CREATION_SCREEN, {
      withLocale: true,
      params: { screenKey },
    });

  /**
   * Does not clear the banner on the way in: answering it is itself what blurs
   * the field, so hiding it here tore the button down between mousedown and
   * mouseup and lost the answer. Editing the address clears it instead.
   */
  const handleEmailBlur = async () => {
    const address = getValues("email").trim();

    // Nothing to ask about an address already kept, one already on the banner,
    // or a value that is not yet plausible.
    if (dismissedAddress.current === address) return;
    if (suggestion?.address === address) return;
    if (!EMAIL_PATTERN.test(address)) return;

    const correction = await checkSuggestion(address);

    // It may land after they answered, or moved on.
    if (
      !correction ||
      dismissedAddress.current === address ||
      getValues("email").trim() !== address
    )
      return;

    setSuggestion({ address, correction });
  };

  const acceptSuggestion = () => {
    if (!suggestion) return;

    // Not dismissed: the corrected address is unchecked, and submit checks it.
    setValue("email", suggestion.correction, { shouldValidate: true });
    setSuggestion(undefined);
  };

  const dismissSuggestion = () => {
    dismissedAddress.current = suggestion?.address;
    setSuggestion(undefined);
  };

  const lookUp = async (address: string) => {
    try {
      const { first_name: firstName } = await checkAvailability(address);

      setAuthIdentity({ email: address, firstName });
      goTo(AUTH_SCREEN_KEYS.LOGIN);
    } catch (error) {
      // Not a failure: a 404 means "no account yet", hence registration.
      if (isUserNotFound(error)) {
        setAuthIdentity({ email: address });
        goTo(AUTH_SCREEN_KEYS.REGISTER);

        return;
      }

      if (!toast.isActive(LOOKUP_FAILED_TOAST_ID))
        toast({
          id: LOOKUP_FAILED_TOAST_ID,
          status: "error",
          description: formatMessage(messages["taskCreation.error.saveFailed"]),
        });
    }
  };

  const onSubmit = async ({ email }: IEmailFormValues) => {
    const address = email.trim();

    // Every time, because pressing Enter never blurs the field.
    if (dismissedAddress.current !== address) {
      const correction =
        suggestion?.address === address
          ? suggestion.correction
          : await checkSuggestion(address);

      setSuggestion(correction ? { address, correction } : undefined);
      // Continue does nothing until they answer it, one way or the other.
      if (correction) return;
    }

    await lookUp(address);
  };

  const {
    onBlur: registerBlur,
    onChange: registerChange,
    ...emailField
  } = register("email", {
    required: formatMessage(messages["taskCreation.validation.required"]),
    pattern: {
      value: EMAIL_PATTERN,
      message: formatMessage(messages["taskCreation.auth.email.invalid"]),
    },
  });

  return (
    <AuthStepLayout
      title={
        <FormattedMessage {...messages["taskCreation.auth.email.title"]} />
      }
      subtitle={
        <FormattedMessage {...messages["taskCreation.auth.email.subtitle"]} />
      }
    >
      <TaskCreationOutcomeModals
        isPublishing={isPublishing}
        publishedRequestId={publishedRequestId}
      />
      {/* Mounted only once there is a prompt: its form reads the prefill as
          `defaultValues`, which a standing instance would have fixed as empty
          long before the provider answered. */}
      {missingInfo && (
        <SsoMissingInfoModal
          isOpen
          onClose={dismissMissingInfo}
          onSubmit={submitMissingInfo}
          email={missingInfo.email}
          emailMissing={missingInfo.emailMissing}
          firstName={missingInfo.firstName}
          lastName={missingInfo.lastName}
        />
      )}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <VStack alignItems="stretch" spacing="16px">
          <AuthFormControl
            htmlFor={EMAIL_FIELD_ID}
            label={
              <FormattedMessage
                {...messages["taskCreation.auth.email.label"]}
              />
            }
            errorMessage={errors.email?.message}
          >
            <InputGroup>
              <InputLeftElement
                height={authFieldStyles.height}
                pointerEvents="none"
              >
                <Icon
                  as={Envelope}
                  width="16px"
                  height="16px"
                  color="gray.500"
                />
              </InputLeftElement>
              <Input
                id={EMAIL_FIELD_ID}
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                {...authFieldStyles}
                placeholder={formatMessage(
                  messages["taskCreation.auth.email.placeholder"]
                )}
                onChange={(event) => {
                  registerChange(event);
                  // Editing the address is what makes a banner stale.
                  setSuggestion(undefined);
                }}
                onBlur={(event) => {
                  registerBlur(event);
                  void handleEmailBlur();
                }}
                {...emailField}
              />
            </InputGroup>
          </AuthFormControl>
          {suggestion && (
            <EmailSuggestion
              suggestion={suggestion.correction}
              onAccept={acceptSuggestion}
              onDismiss={dismissSuggestion}
            />
          )}
          <Button
            type="submit"
            isLoading={isSubmitting}
            isDisabled={isSubmitting || Boolean(pendingProvider)}
            {...authSubmitStyles}
          >
            <FormattedMessage {...messages["taskCreation.cta.continue"]} />
          </Button>
          <SsoButtons
            onSignIn={(provider) => void signIn(provider)}
            pendingProvider={pendingProvider}
            isDisabled={isSubmitting}
          />
          <Text
            fontSize="xs"
            lineHeight="1.5"
            color="gray.600"
            textAlign="center"
          >
            <FormattedMessage
              {...messages["taskCreation.auth.email.terms"]}
              values={{
                /*
                 * A WordPress page whose slug is translated per language
                 * (`wp_routes.condgen`); `/condgen` is the alias nginx 301s to
                 * it. Built by hand — React has no `condgen_path`.
                 */
                link: (...chunks: React.ReactNode[]) => (
                  <Link
                    href={`/${language}/condgen`}
                    isExternal
                    color="pink.500"
                    textDecoration="underline"
                  >
                    {chunks}
                  </Link>
                ),
              }}
            />
          </Text>
        </VStack>
      </form>
    </AuthStepLayout>
  );
};
